import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem } from '@/mocks/menuData';
import ProductDetailModal from './ProductDetailModal';

interface ProductCardProps {
  item: MenuItem;
}

export default function ProductCard({ item }: ProductCardProps) {
  const { t, i18n } = useTranslation();
  const [selectedSize, setSelectedSize] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const hasSizes = item.sizeOptions && item.sizeOptions.length > 0;
  const activePrice = hasSizes ? item.sizeOptions![selectedSize].price : item.price;
  const displayName = item.nameKey ? (t(item.nameKey) !== item.nameKey ? t(item.nameKey) : item.name) : item.name;
  const displayDesc = item.descriptionKey ? (t(item.descriptionKey) !== item.descriptionKey ? t(item.descriptionKey) : item.description) : item.description;

  const waLink = i18n.language === 'es'
    ? 'https://wa.me/19862173604?text=Hola%2C%20quiero%20hacer%20un%20pedido.%20%2F%20Hello%2C%20I%20want%20to%20place%20an%20order.'
    : 'https://wa.me/19862173604?text=Hola%2C%20quiero%20hacer%20un%20pedido.%20%2F%20Hello%2C%20I%20want%20to%20place%20an%20order.';

  return (
    <>
      <article 
        className="group bg-[#111111] border border-white/5 rounded-xl overflow-hidden flex flex-col transition-all duration-300 hover:border-[#F57F25]/30 hover:-translate-y-1 cursor-pointer"
        onClick={() => setIsModalOpen(true)}
      >
        {/* Image */}
        <div className="relative w-full h-48 overflow-hidden shrink-0">
          <img
            src={item.image}
            alt={displayName}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          {/* Price badge */}
          <div className="absolute top-3 right-3 bg-[#F57F25] text-black font-black text-sm px-3 py-1 rounded-full">
            {activePrice}
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 p-4">
          <h3 className="text-white font-bold text-base leading-tight mb-2 group-hover:text-[#F57F25] transition-colors">
            {displayName}
          </h3>
          <p className="text-white/50 text-xs leading-relaxed flex-1 mb-3">
            {displayDesc}
          </p>

          {/* Size Selector */}
          {hasSizes && (
            <div className="mb-3">
              <p className="text-white/40 text-[10px] uppercase tracking-widest font-semibold mb-1.5">{t('size_label')}</p>
              <div className="flex flex-col gap-1.5">
                {item.sizeOptions!.map((opt, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSize(idx);
                    }}
                    className={`flex items-center justify-between w-full text-xs font-semibold px-3 py-2 rounded-lg border transition-all duration-150 cursor-pointer whitespace-nowrap ${
                      selectedSize === idx
                        ? 'border-[#F57F25] bg-[#F57F25]/10 text-[#F57F25]'
                        : 'border-white/10 bg-white/3 text-white/60 hover:border-white/25'
                    }`}
                  >
                    <span>{opt.labelKey && t(opt.labelKey) !== opt.labelKey ? t(opt.labelKey) : opt.label}</span>
                    <span className="font-black">{opt.price}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* WhatsApp Order Button */}
          <div className="flex items-center gap-2 mt-auto">
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex-1 flex items-center justify-center gap-2 bg-[#F57F25] hover:bg-[#e06d15] text-black font-bold text-xs px-4 py-2.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-whatsapp-line text-sm"></i>
              </div>
              {t('btn_order_whatsapp')}
            </a>
            <button 
              className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 text-white/40 group-hover:text-[#F57F25] group-hover:border-[#F57F25]/30 transition-all cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setIsModalOpen(true);
              }}
              title="View details"
            >
              <i className="ri-zoom-in-line text-lg"></i>
            </button>
          </div>
        </div>
      </article>

      {/* Detail Modal */}
      <ProductDetailModal
        product={{ ...item, name: displayName, description: displayDesc, price: activePrice }}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        waLink={waLink}
      />
    </>
  );
}
